import { useState } from 'react';
import Image from 'next/image';

import { Box, Card, Fade, Flex, Heading, Text, useToast } from '@chakra-ui/react';
import { DownloadIcon } from '@udacity/chakra-uds-icons';

import { ButtonLink } from '~/components/button-link';
import { ContentContainer } from '~/components/container';
import { Glyph } from '~/components/glyph';
import { HeadingWithEmphasis } from '~/components/heading-with-emphasis';
import { NotifyMeForm } from '~/components/notify-me-form';
import { Program } from '~/features/program/models/program';
import { ProgramContent } from '~/features/program/models/program-content';

export type DownloadGuideAsideProps = NonNullable<StaticDownloadGuideAsideProps>;

export function DownloadGuideAside({
  downloadGuideSectionContent,
  downloadGuideForm,
  guideTitle,
  guideUrl,
  programKey,
}: DownloadGuideAsideProps) {
  const toast = useToast();
  const [success, setSuccess] = useState<boolean>(false);
  return (
    <Box aria-labelledby='download-guide-section-title' as='aside' isolation='isolate'>
      <ContentContainer>
        <Card
          bgColor='supportBlue.10'
          border='1px solid'
          borderColor='supportBlue.20'
          borderRadius='8px'
          boxShadow='none'
          display='flex'
          flexDirection='row'
          overflow='hidden'
          minHeight={{
            md: '390px',
          }}
          padding={{
            base: 6,
            md: 8,
          }}
        >
          <Flex
            flexDirection='column'
            gap={2}
            flex={{
              md: '0 1 486px',
            }}
          >
            {downloadGuideSectionContent?.title && (
              <HeadingWithEmphasis
                text={downloadGuideSectionContent.title}
                emphasisProps={{
                  color: 'supportBlue.70',
                }}
                headingProps={{
                  id: 'download-guide-section-title',
                  as: 'p',
                  size: {
                    base: 'heading-7',
                    md: 'heading-6',
                  },
                }}
              />
            )}
            {downloadGuideSectionContent?.text && <Text>{downloadGuideSectionContent.text}</Text>}
            {downloadGuideForm && (
              <NotifyMeForm
                isDisabled={success}
                templateForm={downloadGuideForm}
                extraTrackEventProperties={{
                  formid: `PLP lead gen - ${guideTitle}`,
                  product_ids: [programKey],
                }}
                formFieldElementsWrapper={
                  <Flex
                    gap={4}
                    mb={4}
                    flexDir={{
                      base: 'column',
                      lg: 'row',
                    }}
                  />
                }
                successMessageSlot={
                  success ? (
                    <Fade in={true}>
                      <Card
                        alignItems='center'
                        p={{
                          base: 4,
                          xl: 6,
                        }}
                      >
                        <Box mb={2}>
                          <Glyph
                            height={56}
                            name='checkmark-medal-two'
                            width={56}
                            style={{
                              objectPosition: '-6px',
                            }}
                          />
                        </Box>
                        <Heading as='h3' mb={6} size='heading-7'>
                          Thanks!
                        </Heading>
                        {guideUrl && (
                          <ButtonLink
                            linkProps={{ href: guideUrl }}
                            buttonProps={{
                              rightIcon: <DownloadIcon h='24px' w='24px' />,
                              size: 'icon-right',
                              width: { base: '100%', md: 'fit-content' },
                              variant: 'outline',
                            }}
                            isExternal
                          >
                            Download Guide
                          </ButtonLink>
                        )}
                      </Card>
                    </Fade>
                  ) : null
                }
                isFullWidth
                onError={() =>
                  toast({
                    variant: 'error',
                    title: 'Oops. Something went wrong. Please try again.',
                  })
                }
                onSubmit={() => {
                  setSuccess(true);
                  if (guideUrl) {
                    window.open(guideUrl, '_blank');
                  }
                }}
              />
            )}
          </Flex>
          <Box
            marginEnd='-32px'
            marginY='-32px'
            overflow='hidden'
            position='relative'
            display={{
              base: 'none',
              md: 'block',
            }}
            flex={{
              md: '1 1 328px',
            }}
          >
            <Box bottom={0} height='100%' left={0} position='absolute' right={0} top={0} width={387}>
              <Image
                alt=''
                src='/images/experiments/ab_plp_redesign_2025/free-guide.png'
                style={{
                  objectFit: 'cover',
                  objectPosition: '20px 40px',
                }}
                fill
              />
            </Box>
          </Box>
        </Card>
      </ContentContainer>
    </Box>
  );
}

export function transformStaticDownloadGuideAsideProps({
  programContent,
  program,
}: {
  programContent: ProgramContent;
  program: Program;
}) {
  const guide = programContent?.guide;
  const hideDownloadGuideSection = Boolean(programContent?.hideDownloadGuideSection);

  if (!guide || hideDownloadGuideSection) {
    return null;
  }

  if (guide?.downloadGuideSectionContent?.title) {
    guide.downloadGuideSectionContent.title = guide.downloadGuideSectionContent.title.replace(
      /(free guide)/i,
      '**$1**'
    );
  }

  return {
    guideTitle: guide?.title ?? null,
    guideUrl: guide?.file?.asset?.url ?? null,
    downloadGuideSectionContent: guide?.downloadGuideSectionContent ?? null,
    downloadGuideForm: programContent?.downloadGuideForm ?? null,
    programKey: program.key,
  };
}

export type StaticDownloadGuideAsideProps = ReturnType<typeof transformStaticDownloadGuideAsideProps>;
